<template>
  <div class="course-catalog">
    <!-- Header Section -->
    <div class="catalog-header">
      <div class="header-content">
        <h1 class="catalog-title">Learning Catalog</h1>
        <p class="catalog-subtitle">eLearning/credits for Veterinarians – Visual Programs that Replicate Kinesthetic Learning</p>
      </div>
      
      <!-- Search and Filters -->
      <div class="search-filters">
        <div class="search-bar">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search courses, instructors, or topics..."
            class="search-input"
            @input="debouncedSearch"
          />
          <i class="search-icon">🔍</i>
        </div>
        
        <div class="filters">
          <select v-model="selectedCategory" @change="filterCourses" class="filter-select">
            <option value="">All Medical Field / Animal Class</option>
            <option v-for="category in categories" :key="category.id" :value="category.id">
              {{ category.name }}
            </option>
          </select>
          
          <select v-model="selectedType" @change="filterCourses" class="filter-select">
            <option value="">All Categories</option>
            <option value="LECTURE">Lectures</option>
            <option value="SURGERY">Surgeries</option>
            <option value="PRACTICE_MANAGEMENT">Practice Management</option>
            <option value="CONTROLLED_SUBSTANCES">Controlled Substances</option>
          </select>
          
          <select v-model="selectedDifficulty" @change="filterCourses" class="filter-select">
            <option value="">All Delivery Formats</option>
            <option value="ON_DEMAND">On Demand</option>
            <option value="CE_CREDIT_LIVE">CE Credit 'Live'</option>
            <option value="LIVE_STREAM">Live Stream</option>
          </select>
          
          <label class="filter-checkbox">
            <input v-model="showEnrolledOnly" @change="filterCourses" type="checkbox" />
            My Courses Only
          </label>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Loading courses...</p>
    </div>

    <template v-else>
      <!-- Featured Courses Section -->
      <div v-if="featuredCourses.length > 0 && !searchQuery && !selectedCategory && !selectedType" class="featured-section">
        <div class="featured-header">
          <div class="featured-badge">
            <i class="fas fa-star"></i>
            Featured Courses
          </div>
          <p class="featured-subtitle">Highlighted programs selected for you</p>
        </div>
        
        <div class="featured-grid">
          <CourseCard
            v-for="course in featuredCourses"
            :key="course.id"
            :course="course"
            :featured="true"
            @enroll="handleEnroll"
            @view-details="viewCourseDetails"
          />
        </div>
      </div>

      <!-- All Courses Section -->
      <div v-if="filteredCourses.length > 0" class="all-courses-section">
        <div v-if="featuredCourses.length > 0 && !searchQuery && !selectedCategory && !selectedType" class="section-header">
          <h2>All Courses</h2>
          <p>Browse our complete catalog</p>
        </div>
        
        <div class="course-grid">
          <CourseCard
            v-for="course in paginatedCourses"
            :key="course.id"
            :course="course"
            @enroll="handleEnroll"
            @view-details="viewCourseDetails"
          />
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredCourses.length === 0" class="empty-state">
        <div class="empty-icon">📚</div>
        <h3>No courses found</h3>
        <p>Try adjusting your search criteria or filters</p>
        <button @click="clearFilters" class="btn-primary">Clear Filters</button>
      </div>
    </template>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination">
      <button
        @click="currentPage--"
        :disabled="currentPage === 1"
        class="pagination-btn"
      >
        Previous
      </button>
      
      <span class="pagination-info">
        Page {{ currentPage }} of {{ totalPages }}
      </span>
      
      <button
        @click="currentPage++"
        :disabled="currentPage === totalPages"
        class="pagination-btn"
      >
        Next
      </button>
    </div>

    <!-- Course Details Modal -->
    <CourseDetailsModal
      v-if="selectedCourse"
      :course="selectedCourse"
      :visible="showDetailsModal"
      @close="closeDetailsModal"
      @enroll="handleEnroll"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import CourseCard from './CourseCard.vue'
import CourseDetailsModal from './CourseDetailsModal.vue'
import { lmsAPI } from '../../services/lmsAPI.js'

export default {
  name: 'CourseCatalog',
  components: {
    CourseCard,
    CourseDetailsModal
  },
  setup() {
    const router = useRouter()
    const toast = useToast()
    
    // Reactive data
    const courses = ref([])
    const featuredCourses = ref([])
    const categories = ref([])
    const loading = ref(true)
    const searchQuery = ref('')
    const selectedCategory = ref('')
    const selectedType = ref('')
    const selectedDifficulty = ref('')
    const showEnrolledOnly = ref(false)
    const currentPage = ref(1)
    const itemsPerPage = ref(12)
    const selectedCourse = ref(null)
    const showDetailsModal = ref(false)
    
    // Computed properties
    const filteredCourses = computed(() => {
      const featuredIds = new Set(featuredCourses.value.map(c => c.id))
      // Exclude featured courses from the "All Courses" list when the featured
      // section is visible (i.e. no active search/filter) to avoid duplication
      const hideFeatured = !searchQuery.value && !selectedCategory.value && !selectedType.value && !selectedDifficulty.value && !showEnrolledOnly.value
      let filtered = hideFeatured
        ? courses.value.filter(c => !featuredIds.has(c.id))
        : courses.value

      // Search filter
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter(course =>
          course.title.toLowerCase().includes(query) ||
          course.short_description?.toLowerCase().includes(query) ||
          course.instructor_name?.toLowerCase().includes(query) ||
          // Phase 1b — structured instructors
          course.instructors?.some(ins =>
            ins.full_name?.toLowerCase().includes(query) ||
            ins.credentials?.toLowerCase().includes(query)
          ) ||
          course.tags?.some(tag => tag.toLowerCase().includes(query))
        )
      }
      
      // Category filter
      if (selectedCategory.value) {
        filtered = filtered.filter(course => course.category === parseInt(selectedCategory.value))
      }
      
      // Type filter
      if (selectedType.value) {
        filtered = filtered.filter(course => course.course_type === selectedType.value)
      }
      
      // Difficulty filter
      if (selectedDifficulty.value) {
        filtered = filtered.filter(course => course.difficulty_level === selectedDifficulty.value)
      }
      
      // Enrolled only filter
      if (showEnrolledOnly.value) {
        filtered = filtered.filter(course => course.is_enrolled)
      }
      
      return filtered
    })
    
    const totalPages = computed(() => {
      return Math.ceil(filteredCourses.value.length / itemsPerPage.value)
    })
    
    const paginatedCourses = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value
      const end = start + itemsPerPage.value
      return filteredCourses.value.slice(start, end)
    })
    
    // Methods
    const loadCourses = async () => {
      try {
        loading.value = true
        const response = await lmsAPI.getCourses()
        courses.value = response.data
      } catch (error) {
        console.error('Error loading courses:', error)
        toast.error('Failed to load courses')
      } finally {
        loading.value = false
      }
    }
    
    const loadFeaturedCourses = async () => {
      try {
        const response = await lmsAPI.getFeaturedCourses()
        featuredCourses.value = response.data.results || response.data || []
      } catch (error) {
        console.error('Error loading featured courses:', error)
        // Don't show error toast - featured courses are optional
      }
    }
    
    const loadCategories = async () => {
      try {
        const response = await lmsAPI.getCategories()
        categories.value = response.data
      } catch (error) {
        console.error('Error loading categories:', error)
      }
    }
    
    const filterCourses = () => {
      currentPage.value = 1 // Reset to first page when filtering
    }
    
    const clearFilters = () => {
      searchQuery.value = ''
      selectedCategory.value = ''
      selectedType.value = ''
      selectedDifficulty.value = ''
      showEnrolledOnly.value = false
      currentPage.value = 1
    }
    
    const debouncedSearch = debounce(() => {
      filterCourses()
    }, 300)
    
    const handleEnroll = async (course) => {
      try {
        // Check if course is paid
        if (!course.is_free && course.price > 0) {
          await router.push({
            name: 'CourseDetail',
            params: { id: course.id },
            query: { checkout: 'true' },
          })
          return
        }
        
        // Free course - enroll directly
        await lmsAPI.enrollInCourse(course.id)
        
        // Update course enrollment status
        const courseIndex = courses.value.findIndex(c => c.id === course.id)
        if (courseIndex !== -1) {
          courses.value[courseIndex].is_enrolled = true
        }
        
        toast.success(`Successfully enrolled in "${course.title}"!`)
        
        setTimeout(() => {
          router.push({ name: 'CourseDetail', params: { id: course.id } })
        }, 500)
        
      } catch (error) {
        console.error('Error enrolling in course:', error)
        // Extract specific error message from backend response
        const errorMsg = error.response?.data?.course?.[0] || 
                        error.response?.data?.error || 
                        error.response?.data?.message ||
                        'Failed to enroll in course'
        toast.error(errorMsg)
      }
    }
    
    const viewCourseDetails = async (course) => {
      await router.push({ name: 'CourseDetail', params: { id: course.id } })
    }
    
    const closeDetailsModal = () => {
      showDetailsModal.value = false
      selectedCourse.value = null
    }
    
    // Utility function for debouncing
    function debounce(func, wait) {
      let timeout
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout)
          func(...args)
        }
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
      }
    }
    
    // Watch for filter changes to reset pagination
    watch([selectedCategory, selectedType, selectedDifficulty, showEnrolledOnly], () => {
      currentPage.value = 1
    })
    
    // Lifecycle
    onMounted(() => {
      loadCourses()
      loadFeaturedCourses()
      loadCategories()
    })
    
    return {
      courses,
      featuredCourses,
      categories,
      loading,
      searchQuery,
      selectedCategory,
      selectedType,
      selectedDifficulty,
      showEnrolledOnly,
      currentPage,
      totalPages,
      filteredCourses,
      paginatedCourses,
      selectedCourse,
      showDetailsModal,
      loadCourses,
      filterCourses,
      clearFilters,
      debouncedSearch,
      handleEnroll,
      viewCourseDetails,
      closeDetailsModal
    }
  }
}
</script>

<style scoped>
.course-catalog {
  width: 100%;
  margin: 0;
  padding: 0;
}

.catalog-header {
  margin-bottom: 2rem;
}

.header-content {
  text-align: center;
  margin-bottom: 2rem;
}

.catalog-title {
  font-size: 2.5rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.catalog-subtitle {
  font-size: 1.1rem;
  color: #6b7280;
}

.catalog-tagline {
  font-size: 0.95rem;
  color: #dc2626;
  font-style: italic;
  margin-top: 0.5rem;
}

.search-filters {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.search-bar {
  position: relative;
  margin-bottom: 1rem;
}

.search-input {
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.2s;
}

.search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.filters {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
}

.filter-select {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  min-width: 150px;
}

.filter-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.loading-state {
  text-align: center;
  padding: 4rem 2rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Featured Courses Section */
.featured-section {
  margin-bottom: 3rem;
  padding: 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
}

.featured-header {
  text-align: center;
  margin-bottom: 2rem;
  color: white;
}

.featured-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  font-size: 1.25rem;
  font-weight: bold;
  color: white;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 0.75rem;
}

.featured-badge i {
  color: #fbbf24;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.featured-subtitle {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.featured-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  align-items: stretch;
}

.all-courses-section {
  margin-top: 3rem;
}

.section-header {
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
}

.section-header h2 {
  font-size: 2rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.section-header p {
  font-size: 1.1rem;
  color: #6b7280;
  margin: 0;
}

.course-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  align-items: stretch;
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #374151;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  margin-top: 1rem;
  transition: background-color 0.2s;
}

.btn-primary:hover {
  background: #2563eb;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  color: #6b7280;
  font-weight: 500;
}

@media (max-width: 768px) {
  .course-catalog {
    padding: 0;
  }
  
  .catalog-title {
    font-size: 2rem;
  }
  
  .filters {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-select {
    min-width: auto;
  }
  
  .course-grid {
    grid-template-columns: 1fr;
  }
}
</style>
